import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAuditTrailByid } from "../../../../../redux/auditTrailSlice";
import { getAllVersionHistories } from "../../../../../redux/versionHistorySlice";
import PreviewProcedureRevision from "./PreviewProcedureRevision";
import PreviewSection from "./PreviewSection";
import PreviewStep from "./PreviewStep";
import PreviewSubStep from "./PreviewSubStep";
import PreviewAttachment from "./PreviewAttachment";
import PreviewVideo from "./PreviewVideo";
import PreviewRichText from "./PreviewRichText";
import PreviewCanvas from "./PreviewCanvas";
import RevisionPreview from "../../MoveAction/RevisionPreview/RevisionPreview";
import VersionHistoryPopup from "../../../../../common/VersionHistoryPopup/VersionHistoryPopup";
import Loader from "../../../../../common/Loader";
import RenderRevisionNodes from "../../../../Documents/components/RenderRevisionNodes";
import { processContentData } from "../../utils/previewUtils";
import {
  getSnapshotParseOptionsForAuditRow,
  resolveSnapshot,
} from "../../utils/fetchSnapshotContent";
import { narrowReviewPreviewContent } from "../../utils/auditTrailNarrowTree";
import {
  mergeAuditTrailRowVersionContentDeep,
  parsePartRevFromAuditActionMessage,
} from "../../../utils/auditTrailProcedureIdentity";

/** Normalize axios / RTK thunk body shapes (results vs data.results vs array). */
function pickAuditRowFromGetByIdPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  const row =
    payload.results ??
    payload.data?.results ??
    payload.data?.data ??
    payload.data;
  if (Array.isArray(row)) return row[0] ?? null;
  return row;
}

function mergeAuditVersionContentFlat(vc) {
  if (!vc || typeof vc !== "object" || Array.isArray(vc)) return {};
  const inner = vc.version_content;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return { ...vc, ...inner };
  }
  return { ...vc };
}

function cloneSnapshotRows(rows) {
  if (!Array.isArray(rows)) return null;
  try {
    return JSON.parse(JSON.stringify(rows));
  } catch {
    return rows.slice();
  }
}

const ComparisonLayout = () => {
  const dispatch = useDispatch();
  const { audit_trail_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [revData, setRevData] = useState({ version_content: {} });
  const [comparisonData, setComparisonData] = useState({ version_content: {} });
  const [selectedView, setSelectedView] = useState("new");
  const [comparisonVersionId, setComparisonVersionId] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const [previousSnapshotData, setPreviousSnapshotData] = useState({
    data: null,
    meta: null,
    loaded: false,
    error: null,
  });
  const [newSnapshotData, setNewSnapshotData] = useState({
    data: null,
    meta: null,
    loaded: false,
    error: null,
  });

  const { versionHistories, loading: isLoadingVersions } = useSelector(
    (state) => state.versionHistory,
  );

  /** getAuditTrailByid is not tied to redux `audittrail.loading` — local gate for first load. */
  const [isAuditAndSnapshotsResolving, setIsAuditAndSnapshotsResolving] =
    useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsAuditAndSnapshotsResolving(true);
      try {
        const resp = await dispatch(getAuditTrailByid({ id: audit_trail_id }));
        if (getAuditTrailByid.rejected.match(resp)) {
          if (!cancelled) {
            setPreviousSnapshotData((s) => ({ ...s, loaded: true, error: "Failed to load" }));
            setNewSnapshotData((s) => ({ ...s, loaded: true, error: "Failed to load" }));
          }
          return;
        }
        const auditData = pickAuditRowFromGetByIdPayload(resp.payload);
        if (!auditData || typeof auditData !== "object") {
          if (!cancelled) {
            setPreviousSnapshotData((s) => ({ ...s, loaded: true, error: "Invalid response" }));
            setNewSnapshotData((s) => ({ ...s, loaded: true, error: "Invalid response" }));
          }
          return;
        }
        if (cancelled) return;
        setRevData(auditData);

        const snapshotParseOpts = {
          ...getSnapshotParseOptionsForAuditRow(auditData),
        };

        if (auditData.previous_content_snapshot) {
          try {
            const { rows, meta } = await resolveSnapshot(
              auditData.previous_content_snapshot,
              "original",
              snapshotParseOpts,
            );
            if (!cancelled) {
              setPreviousSnapshotData({
                data: cloneSnapshotRows(rows),
                meta,
                loaded: true,
                error: null,
              });
            }
          } catch (e) {
            if (!cancelled) {
              setPreviousSnapshotData({
                data: null,
                meta: null,
                loaded: true,
                error: e?.message || "Load failed",
              });
            }
          }
        } else {
          if (!cancelled) {
            setPreviousSnapshotData({
              data: null,
              meta: null,
              loaded: true,
              error: "No Data",
            });
          }
        }

        if (auditData.new_content_snapshot) {
          try {
            const { rows, meta } = await resolveSnapshot(
              auditData.new_content_snapshot,
              "new",
              snapshotParseOpts,
            );
            if (!cancelled) {
              setNewSnapshotData({
                data: cloneSnapshotRows(rows),
                meta,
                loaded: true,
                error: null,
              });
            }
          } catch (e) {
            if (!cancelled) {
              setNewSnapshotData({
                data: null,
                meta: null,
                loaded: true,
                error: e?.message || "Load failed",
              });
            }
          }
        } else {
          if (!cancelled) {
            setNewSnapshotData({
              data: null,
              meta: null,
              loaded: true,
              error: "No Data",
            });
          }
        }

      } catch (e) {
        if (!cancelled) {
          setPreviousSnapshotData((s) => ({ ...s, loaded: true, error: e?.message || "Error" }));
          setNewSnapshotData((s) => ({ ...s, loaded: true, error: e?.message || "Error" }));
        }
      } finally {
        if (!cancelled) setIsAuditAndSnapshotsResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, audit_trail_id]);

  useEffect(() => {
    if (!revData?.version_id) return;
    (async () => {
      await dispatch(getAllVersionHistories({ versionId: revData.version_id }));
    })();
  }, [dispatch, revData?.version_id]);

  useEffect(() => {
    const compareWith = searchParams.get("compareWith");
    if (compareWith && versionHistories && versionHistories.length > 0) {
      setComparisonVersionId(compareWith);
      const comparisonVersion = versionHistories.find(
        (v) => v.version_id === compareWith,
      );
      if (comparisonVersion) {
        setComparisonData(comparisonVersion);
      } else {
        const versionWithAuditTrail = versionHistories.find(
          (v) => v.audit_trail_id === compareWith,
        );
        if (versionWithAuditTrail) {
          setComparisonData(versionWithAuditTrail);
        } else {
          (async () => {
            try {
              const resp = await dispatch(getAuditTrailByid({ id: compareWith }));
              const row = pickAuditRowFromGetByIdPayload(resp.payload);
              if (row) setComparisonData(row);
            } catch {
              // optional secondary fetch
            }
          })();
        }
      }
    } else if (compareWith) {
      setComparisonVersionId(compareWith);
    }
  }, [searchParams, dispatch, versionHistories]);

  const currentSnapshotData =
    selectedView === "original" ? previousSnapshotData : newSnapshotData;
  const isContentLoaded = currentSnapshotData.loaded;
  const rawContentData = currentSnapshotData.data;
  const processedData = processContentData(
    Array.isArray(rawContentData) ? rawContentData : [],
  );
  const previousMeta = previousSnapshotData.meta;
  const newMeta = newSnapshotData.meta;
  const previewContentData = narrowReviewPreviewContent(
    processedData,
    revData,
    selectedView,
    previousMeta,
    newMeta,
  );
  const hasContent = previewContentData && previewContentData.length > 0;

  const revisionTreeForPopup = useMemo(() => {
    const rows =
      newSnapshotData.data && Array.isArray(newSnapshotData.data)
        ? newSnapshotData.data
        : previousSnapshotData.data && Array.isArray(previousSnapshotData.data)
          ? previousSnapshotData.data
          : [];
    return processContentData(rows);
  }, [newSnapshotData.data, previousSnapshotData.data]);

  const versionHistoryBreadcrumbFallback = useMemo(() => {
    const m = mergeAuditTrailRowVersionContentDeep(revData) || {};
    const pr = revData?.procedure_revision || {};
    const msg = [revData?.action, revData?.action_to_show, m?.action_to_show]
      .filter((s) => typeof s === "string" && s.trim())
      .join(" ");
    const parsed = parsePartRevFromAuditActionMessage(msg);
    const rawPn =
      m.partNumber ??
      m.part_number ??
      m.procedure_number ??
      pr.partNumber ??
      pr.part_number ??
      parsed.partNumber;
    const code = rawPn ? String(rawPn).trim() : "";
    const r =
      m.revision ??
      m.rev ??
      pr.revision ??
      pr.rev ??
      pr.label ??
      parsed.revision ??
      "";
    const revPart =
      r != null && String(r).trim() !== "" ? `Rev ${String(r).trim()}` : "";
    return [code, revPart].filter(Boolean).join(" > ");
  }, [revData]);

  const vc = revData.version_content || {};
  const action_to_show = mergeAuditVersionContentFlat(vc).action_to_show;

  const displayData =
    selectedView === "original"
      ? comparisonData?.version_content || {}
      : revData?.version_content || {};
  const data = {
    ...displayData,
    vrsn_id:
      selectedView === "original"
        ? comparisonData?.version_id
        : revData?.version_id,
  };

  const displayTitle = `${revData.version_content?.pageTitle || ""} (Version ${revData.version_id || ""}) Review Against ${comparisonData.version_content?.pageTitle || ""} (Version ${comparisonVersionId || ""})`;

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCompareWithEarlierVersion = async () => {
    if (!revData?.version_id) return;
    await dispatch(getAllVersionHistories({ versionId: revData.version_id }));
    setShowVersionHistory(true);
  };

  const compareEarlierDisabled = !revData?.version_id || isLoadingVersions;

  const useRevisionPreview =
    action_to_show?.includes("_MOVED") ||
    action_to_show?.includes("_COPIED") ||
    (revData.version_content?.newContent &&
      revData.version_content?.originingalContent);

  const snapshotsReady = previousSnapshotData.loaded && newSnapshotData.loaded;
  const useS3TreePreview =
    !useRevisionPreview &&
    snapshotsReady &&
    (revData.new_content_snapshot || revData.previous_content_snapshot);

  if (isAuditAndSnapshotsResolving) {
    return <Loader text="Loading comparison data..." />;
  }

  if (!revData?.version_id) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Unable to load audit trail data.</div>
      </div>
    );
  }

  return (
    <>
      <header className="appbar">
        <button
          type="button"
          className="appbar-button-secondary"
          onClick={handleCancel}
          style={{
            backgroundColor: "#f1f3f4",
            color: "#47515c",
            border: "none",
            padding: "8px 12px",
            cursor: "pointer",
            borderRadius: "4px",
            marginLeft: "15px",
            marginRight: "15px",
          }}
        >
          Cancel
        </button>

        <div
          className="view-toggle"
          style={{
            width: "300px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginLeft: "20px",
          }}
        >
          <label
            style={{
              fontSize: "15px",
              display: "flex",
              alignItems: "center",
              gap: "2px",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="view"
              value="original"
              checked={selectedView === "original"}
              onChange={() => setSelectedView("original")}
              style={{ cursor: "pointer" }}
            />
            <span
              style={{
                fontSize: "10px",
                whiteSpace: "nowrap",
                marginTop: "3px",
              }}
            >
              View Original
            </span>
          </label>
          <label
            style={{
              fontSize: "15px",
              display: "flex",
              alignItems: "center",
              gap: "2px",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="view"
              value="new"
              checked={selectedView === "new"}
              onChange={() => setSelectedView("new")}
              style={{ cursor: "pointer" }}
            />
            <span
              style={{
                fontSize: "10px",
                whiteSpace: "nowrap",
                marginTop: "3px",
              }}
            >
              View New
            </span>
          </label>
        </div>

        <h2
          style={{
            fontSize: "20px",
            color: "#0D6EFD",
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          {displayTitle}
        </h2>

        <div className="appbar-buttons">
          <button
            type="button"
            className="appbar-button-secondary"
            onClick={handleCompareWithEarlierVersion}
            disabled={compareEarlierDisabled}
            style={{
              opacity: compareEarlierDisabled ? 0.6 : 1,
              cursor: compareEarlierDisabled ? "not-allowed" : "pointer",
            }}
          >
            Compare with Earlier Version
          </button>
        </div>
      </header>

      {useRevisionPreview ? (
        <RevisionPreview
          hideAppBar={true}
          externalSelectedView={selectedView}
          externalSetSelectedView={setSelectedView}
          externalAuditTrailData={revData}
        />
      ) : useS3TreePreview ? (
        !isContentLoaded ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <div>
              Loading {selectedView === "original" ? "original" : "new"}{" "}
              content...
            </div>
          </div>
        ) : currentSnapshotData.error && currentSnapshotData.error !== "No Data" ? (
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#dc3545",
              fontSize: "16px",
            }}
          >
            {currentSnapshotData.error}
          </div>
        ) : hasContent ? (
          <div style={{ padding: "20px" }}>
            <RenderRevisionNodes
              key={`${audit_trail_id}-${selectedView}`}
              preloadedData={previewContentData}
              showCheckbox={false}
              disabled={true}
            />
          </div>
        ) : (
          <div
            style={{ padding: "40px", textAlign: "center", color: "#666", fontSize: "16px" }}
          >
            No Data
          </div>
        )
      ) : !revData?.new_content_snapshot ? (
        <div
          style={{ padding: "40px", textAlign: "center", color: "#666", fontSize: "16px" }}
        >
          No Data
        </div>
      ) : action_to_show === "UPDATE_PROCEDURE_REVISION" ? (
        <PreviewProcedureRevision version_content={data} />
      ) : action_to_show?.includes("_SECTION") ? (
        <PreviewSection version_content={data} />
      ) : action_to_show?.includes("_STEP") ? (
        <PreviewStep version_content={data} />
      ) : action_to_show?.includes("_SUBSTEP") ? (
        <PreviewSubStep version_content={data} />
      ) : data.type?.includes("attachment") ? (
        <PreviewAttachment version_content={data} />
      ) : action_to_show?.includes("_CANVAS") ? (
        <PreviewCanvas version_content={data} />
      ) : action_to_show?.includes("_VIDEO") ||
        action_to_show?.includes("VIDEO_") ? (
        <PreviewVideo version_content={data} />
      ) : action_to_show?.includes("_RICH_TEXT") ||
        action_to_show?.includes("RICH-TEXT_") ? (
        <PreviewRichText version_content={data} />
      ) : (
        ""
      )}

      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          versionHistories={versionHistories || []}
          mode="edit"
          versionId={revData.version_id}
          nodeId={
            revData.version_content?.element_id ||
            revData.version_content?.step_tree_node_id
          }
          nodeIdField="element_id"
          breadcrumbText={
            versionHistoryBreadcrumbFallback ||
            String(revData.version_content?.breadcrumb_text || "").trim()
          }
          revisionTreeNodes={revisionTreeForPopup}
          comparisonSelect
          initialFilterType="all"
          currentVersionId={comparisonVersionId}
          onViewDetails={(selectedVersion) => {
            setShowVersionHistory(false);
            navigate(
              `/audit-trail/comparison/${audit_trail_id}?compareWith=${selectedVersion.version_id}`,
            );
          }}
        />
      )}
    </>
  );
};

export default ComparisonLayout;
